import { VerifyMethodType } from "shared/types";
import { ReasonCode } from "types/landlord/reports";
import { UploadedIncomeSource } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { W2Item } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/w2";

export interface W2InscribeAdditionalData {
  type_mismatch?: boolean;
  provided_type?: string;
  is_name_present?: boolean;
  low_quality_score?: boolean;
  not_w2?: boolean;
  wrong_year?: boolean;
  high_risk_level?: boolean;
  risk_level?: "LOW" | "MEDIUM" | "HIGH" | "NOT_APPLICABLE";
  risk_assessment_summary?: string;
}

export const getW2InscribeReasonCodes = (
  additionalData?: W2InscribeAdditionalData | null
): ReasonCode[] => {
  if (!additionalData) return [];

  const warnings: ReasonCode[] = [];

  if (additionalData.type_mismatch)
    warnings.push({
      code: "",
      confidence: "HIGH",
      description: additionalData.provided_type
        ? `Wrong document type (${additionalData.provided_type})`
        : "Wrong document type"
    });

  if (additionalData.not_w2)
    warnings.push({
      code: "",
      confidence: "HIGH",
      description: "Tax form is not a W2"
    });

  if (additionalData.wrong_year)
    warnings.push({
      code: "",
      confidence: "HIGH",
      description: "W2 is for the wrong tax year"
    });

  if (additionalData.low_quality_score)
    warnings.push({
      code: "",
      confidence: "HIGH",
      description: "The file is of poor quality"
    });

  if (additionalData.is_name_present === false)
    warnings.push({
      code: "",
      confidence: "HIGH",
      description: "No employee name detected on document"
    });

  return warnings;
};

export const mapW2FormsWithSourceData = (item: W2Item) => {
  const additional_data = item.income_source_data?.additional_data;
  const trust_score = item.income_source_data?.raw_data?.trust_score;
  const source_status = item.status;

  const forms =
    item.data?.document?.income_sources?.flatMap((incSource) =>
      incSource.forms.map((form) => ({
        ...form,
        verifyType: VerifyMethodType.W2,
        income_source_id: item.income_source_id,
        additional_data,
        trust_score,
        source_status
      }))
    ) ?? [];

  if (!forms.length && additional_data)
    return [
      {
        verifyType: VerifyMethodType.W2,
        income_source_id: item.income_source_id,
        additional_data,
        trust_score,
        source_status
      } as (typeof forms)[number]
    ];

  return forms;
};

const FAILED_W2_FALLBACK_REASONS = ["type_mismatch"];

export const buildFailedW2Row = (
  source: UploadedIncomeSource
): ReturnType<typeof mapW2FormsWithSourceData>[number] =>
  ({
    verifyType: VerifyMethodType.W2,
    income_source_id: source.id,
    source_status: source.status,
    trust_score: source.trust_score,
    rejection_reason: source.rejection_reason?.length
      ? source.rejection_reason
      : FAILED_W2_FALLBACK_REASONS,
    asset: source.asset ? { asset: source.asset.asset } : undefined
  }) as unknown as ReturnType<typeof mapW2FormsWithSourceData>[number];
